import React, {useCallback, useEffect, useRef, useState} from "react";
import classNames from "classnames";
import TimePicker from "./TimePicker";
import ARIcon from "../../../../ARIcon";
import {generateEvent} from "../../../utils";
import {useController} from "react-hook-form";
import {MESSAGES_TYPES} from "../../../schemes/messages";
import ValidationFeedback from "../../../ValidationFeedback";
import {formatTime, lastRegExpSymbolIndexOf, timeMask} from "./ARTimeInput.model";
import {
  CButton,
  CFormGroup,
  CInput,
  CInputGroup,
  CInputGroupAppend,
  CInputGroupPrepend,
  CInputGroupText
} from "@coreui/react";
import "./ARTimeInput.scss";

const ARTimeInput = (props) => {
  const {control, id, name, trigger, className, prependComponent, ...restProps} = props;
  const {
    field: {value, ref, onChange, ...inputProps},
    fieldState: {invalid, isDirty, error},
    formState: {isSubmitted}
  } = useController({name, control});

  const inputRef = useRef(null);
  const currentValue = value || timeMask;
  const oldValue = useRef(value || timeMask);
  const [showTimePicker, setShowTimePicker] = useState(false);

  const onTimeInputChange = useCallback((event) => {
    oldValue.current = currentValue;
    const inputValue = event.target.value;
    const formattedTime = formatTime(inputValue);
    const newValue = formattedTime === timeMask ? null : formattedTime;
    const newEvent = generateEvent({event, newValue});

    onChange(newEvent);
    trigger && trigger();
  }, [onChange, currentValue, trigger]);

  const onTimeInputClick = (event) => {
    event.preventDefault();
    setCursorPosition();
  };

  const hideTimePicker = useCallback(() => setShowTimePicker(false), []);

  const checkDeleting = useCallback(() => {
    return currentValue.replace(/\D/g, "").length - oldValue.current.replace(/\D/g, "").length < 0;
  }, [currentValue]);

  const setCursorPosition = useCallback(() => {
    if (inputRef.current) {
      const isDeleting = checkDeleting();
      const match = isDeleting ? lastRegExpSymbolIndexOf(/\d/, currentValue) : currentValue.match(/–/);
      const position = match?.index ?? (isDeleting ? 0 : currentValue.length);
      inputRef.current.setSelectionRange(position, position);
    }
  }, [checkDeleting, currentValue]);

  const onTimePickerClick = () => {
    setShowTimePicker(!showTimePicker);
  };

  useEffect(() => {
    setCursorPosition();
  }, [currentValue, setCursorPosition]);

  return (
    <CFormGroup className={classNames(className)}>
      <CInputGroup>
        {prependComponent &&
        <CInputGroupPrepend>
          <CInputGroupText>
            {prependComponent}
          </CInputGroupText>
        </CInputGroupPrepend>
        }
        <CInput
          id={id}
          name={name}
          inputMode="numeric"
          className="field_right-border-radius"
          placeholder={timeMask}
          onChange={onTimeInputChange}
          onClick={onTimeInputClick}
          value={formatTime(currentValue)}
          innerRef={ref}
          valid={(isDirty || isSubmitted) && !invalid}
          invalid={invalid && error.message.type === MESSAGES_TYPES.DANGER}
          {...inputProps}
          {...restProps}
        />
        <CInputGroupAppend>
          <CButton
            color="primary"
            className="ARTimeInput__time-picker-button"
            onClick={onTimePickerClick}
          >
            <ARIcon name="cilClock"/>
          </CButton>
        </CInputGroupAppend>
        <TimePicker
          name={`TimePicker_${name}`}
          show={showTimePicker}
          value={currentValue}
          onChange={onTimeInputChange}
          onBlur={hideTimePicker}
          required={false}
        />
        <ValidationFeedback error={error}/>
      </CInputGroup>
    </CFormGroup>
  );
};

export default ARTimeInput;